/**
 * ReportBuilderPanel — compose, save, run, and print BI reports.
 *
 * A report is a saved document (lib/reports.js — CRUD over /api/reports) with a
 * default data source, named parameters, and an ordered list of blocks:
 *   • text   — headings / notes (mini-markdown)
 *   • query  — a SELECT rendered as a table or a chart
 *   • metric — a single KPI value (first cell of a SELECT)
 * Parameters are bound as MySQL @vars (setSessionVars) before each block runs,
 * so a block's SQL references @param directly. Block queries execute client-side
 * through the existing query-session API (auth + binding already handled there);
 * the backend only stores the definition. Three modes: list / edit / view, with
 * a print-to-PDF path (browser print + @media print CSS).
 */
import { createSignal, createMemo, createResource, createEffect, For, Show, Switch, Match } from 'solid-js';
import { createStore, produce, reconcile } from 'solid-js/store';
import { api } from '../lib/api.js';
import { listReports, getReport, createReport, updateReport, deleteReport, rid } from '../lib/reports.js';
import { createQuerySession, executeQuery, closeQuerySession, setSessionVars } from '../lib/querysession.js';
import { buildChartModel } from '../lib/chart.js';
import Chart from '../components/Chart.jsx';
import ChartView from '../components/ChartView.jsx';
import { relTime } from '../lib/format.js';
import { toast } from '../lib/toast.js';

const blankReport = () => ({ name: '', description: '', connectionId: '', database: '', params: [], blocks: [] });

export default function ReportBuilderPanel() {
  const [mode, setMode] = createSignal('list');          // 'list' | 'edit' | 'view'
  const [currentId, setCurrentId] = createSignal(null);  // report id, or null for an unsaved new report
  const [report, setReport] = createStore(blankReport());
  const [paramValues, setParamValues] = createSignal({}); // name -> value (view mode)
  const [results, setResults] = createStore({});          // blockId -> { loading, error, columns, rows }
  const [running, setRunning] = createSignal(false);
  const [dirty, setDirty] = createSignal(false);

  const [reportList, { refetch: refetchList }] = createResource(listReports);
  const [conns] = createResource(async () => {
    try { return (await api.get('/api/connections')).connections || []; } catch { return []; }
  });
  const [dbList, setDbList] = createSignal([]);
  // dbReq: only the latest connection's response lands (a slow earlier one would
  // otherwise overwrite the list for the connection picked after it).
  let dbReq = 0;
  createEffect(() => {
    const cid = report.connectionId;
    const req = ++dbReq;
    if (!cid) { setDbList([]); return; }
    api.get(`/api/connections/${cid}/databases`)
      .then((r) => { if (req === dbReq) setDbList((r.databases || []).map((d) => d.name)); })
      .catch(() => { if (req === dbReq) setDbList([]); });
  });

  const markDirty = () => setDirty(true);

  // ── load / new / save ───────────────────────────────────────────────────────
  // reportGen is bumped whenever a different report takes the panel: a run still
  // in flight for the previous one stops at its next block and drops its results
  // (it must never run report B's blocks on report A's session/params).
  let reportGen = 0;
  const loadReport = async (id) => {
    try {
      const r = await getReport(id);
      const def = typeof r.definition === 'string' ? JSON.parse(r.definition || '{}') : (r.definition || {});
      reportGen++;
      setReport(reconcile({
        name: r.name, description: r.description || '',
        connectionId: def.connectionId || '', database: def.database || '',
        params: def.params || [], blocks: def.blocks || [],
      }));
      setCurrentId(id);
      setResults(reconcile({}));
      // Seed param values from defaults, then open in view mode and run.
      const pv = {};
      for (const p of (def.params || [])) pv[p.name] = p.default ?? '';
      setParamValues(pv);
      setDirty(false);
      setMode('view');
      queueMicrotask(runAll);
    } catch (e) { toast.error(`Open failed: ${e.message || e}`); }
  };

  const newReport = () => {
    reportGen++;
    setReport(reconcile(blankReport()));
    setCurrentId(null);
    setResults(reconcile({}));
    setParamValues({});
    setDirty(false);
    setMode('edit');
  };

  const definitionJSON = () => ({
    connectionId: report.connectionId, database: report.database,
    params: report.params, blocks: report.blocks,
  });

  const save = async (asNew = false) => {
    if (!report.name.trim()) { toast.error('Give the report a name'); return; }
    const body = { name: report.name.trim(), description: report.description, definition: definitionJSON() };
    try {
      let saved;
      if (asNew || currentId() == null) saved = await createReport(body);
      else saved = await updateReport(currentId(), body);
      setCurrentId(saved.id);
      setDirty(false);
      refetchList();
      toast.success('Report saved');
    } catch (e) { toast.error(`Save failed: ${e.message || e}`); }
  };

  const removeReport = async (id, name) => {
    if (!confirm(`Delete report "${name}"? This cannot be undone.`)) return;
    try { await deleteReport(id); refetchList(); if (currentId() === id) backToList(); }
    catch (e) { toast.error(`Delete failed: ${e.message || e}`); }
  };

  const backToList = () => { setMode('list'); refetchList(); };

  // ── parameters ──────────────────────────────────────────────────────────────
  const addParam = () => { setReport('params', (p) => [...p, { name: `param${p.length + 1}`, label: '', type: 'text', default: '', options: [] }]); markDirty(); };
  const setParam = (i, key, val) => { setReport('params', i, key, val); markDirty(); };
  const removeParam = (i) => { setReport('params', (p) => p.filter((_, k) => k !== i)); markDirty(); };
  const coerceParam = (p, v) => {
    const raw = v ?? p.default ?? '';
    if (p.type === 'number') { const n = Number(raw); return Number.isFinite(n) ? n : 0; }
    return String(raw);
  };

  // ── blocks ──────────────────────────────────────────────────────────────────
  const addBlock = (type) => {
    const base = { id: rid(), type };
    const b = type === 'text' ? { ...base, content: '## Heading\nText…' }
      : type === 'metric' ? { ...base, title: 'Metric', sql: 'SELECT COUNT(*) FROM ', label: '' }
      : { ...base, title: 'Query', sql: 'SELECT * FROM ', view: 'table', chart: { type: 'bar', x: '', y: [], series: '' } };
    setReport('blocks', (bs) => [...bs, b]);
    markDirty();
  };
  const setBlock = (i, key, val) => { setReport('blocks', i, key, val); markDirty(); };
  const removeBlock = (i) => { const id = report.blocks[i].id; setReport('blocks', (bs) => bs.filter((_, k) => k !== i)); setResults(produce((r) => { delete r[id]; })); markDirty(); };
  const moveBlock = (i, dir) => {
    const j = i + dir; if (j < 0 || j >= report.blocks.length) return;
    setReport('blocks', produce((bs) => { const t = bs[i]; bs[i] = bs[j]; bs[j] = t; })); markDirty();
  };

  // ── running blocks (view mode = all; edit mode = single preview) ─────────────
  const buildVars = () => {
    const vars = {};
    for (const p of report.params) vars[p.name] = coerceParam(p, paramValues()[p.name]);
    return vars;
  };
  // `gen` is the reportGen the run started under; a stale run writes nothing.
  const runOne = async (sid, b, gen) => {
    if (b.type === 'text') return;
    setResults(b.id, { loading: true, error: null, columns: [], rows: [] });
    try {
      const r = await executeQuery(sid, b.sql, true);
      if (gen !== reportGen) return;
      const rs = (r.resultSets || [])[0];
      setResults(b.id, { loading: false, error: null, columns: (rs?.columns || []).map((c) => c.name), rows: rs?.rows || [] });
    } catch (e) {
      if (gen !== reportGen) return;
      setResults(b.id, { loading: false, error: String(e.message || e), columns: [], rows: [] });
    }
  };

  // Runs can overlap (a newly loaded report starts while the old one finishes),
  // so `running` stays on until the LAST of them ends.
  let activeRuns = 0;
  const beginRun = () => { activeRuns++; setRunning(true); };
  const endRun = () => { if (--activeRuns === 0) setRunning(false); };

  const runAll = async () => {
    if (!report.connectionId) { toast.error('Pick a connection for this report'); return; }
    const gen = reportGen;
    const blocks = [...report.blocks]; // this run's blocks, fixed at start
    beginRun();
    let sid = null;
    try {
      const s = await createQuerySession(report.connectionId, report.database || null);
      sid = s.sessionId;
      const vars = buildVars();
      if (Object.keys(vars).length) await setSessionVars(sid, vars);
      for (const b of blocks) {
        if (gen !== reportGen) break; // another report took over — stop here
        await runOne(sid, b, gen);
      }
    } catch (e) {
      if (gen === reportGen) toast.error(`Run failed: ${e.message || e}`);
    } finally {
      if (sid) closeQuerySession(sid);
      endRun();
    }
  };

  const previewBlock = async (b) => {
    if (!report.connectionId) { toast.error('Pick a connection for this report first'); return; }
    const gen = reportGen;
    beginRun();
    let sid = null;
    try {
      const s = await createQuerySession(report.connectionId, report.database || null);
      sid = s.sessionId;
      const vars = buildVars();
      if (Object.keys(vars).length) await setSessionVars(sid, vars);
      await runOne(sid, b, gen);
    } catch (e) { if (gen === reportGen) toast.error(`Preview failed: ${e.message || e}`); }
    finally { if (sid) closeQuerySession(sid); endRun(); }
  };

  // ── print ───────────────────────────────────────────────────────────────────
  const printReport = () => {
    document.body.classList.add('rb-printing');
    const cleanup = () => { document.body.classList.remove('rb-printing'); window.removeEventListener('afterprint', cleanup); };
    window.addEventListener('afterprint', cleanup);
    setTimeout(() => window.print(), 60);
    setTimeout(cleanup, 4000); // fallback if afterprint never fires
  };

  // ── render helpers ────────────────────────────────────────────────────────────
  const blockResult = (id) => results[id];

  return (
    <div class="rb-panel">
      {/* ── LIST ──────────────────────────────────────────────────────────────── */}
      <Show when={mode() === 'list'}>
        <div class="rb-list">
          <div class="rb-list-head">
            <h2>Reports</h2>
            <button class="btn btn-primary" onClick={newReport}>＋ New report</button>
          </div>
          <Show when={reportList.loading}><div class="muted rb-pad">Loading…</div></Show>
          <Show when={reportList() && reportList().length === 0}>
            <div class="rb-empty">No reports yet. Create one to combine queries, charts, and notes into a runnable, printable document.</div>
          </Show>
          <div class="rb-cards">
            <For each={reportList() || []}>
              {(r) => (
                <div class="rb-card" onClick={() => loadReport(r.id)}>
                  <div class="rb-card-name">{r.name}</div>
                  <Show when={r.description}><div class="rb-card-desc">{r.description}</div></Show>
                  <div class="rb-card-meta muted">updated {relTime(r.updatedAt)}</div>
                  <button class="rb-card-del" title="Delete report" onClick={(e) => { e.stopPropagation(); removeReport(r.id, r.name); }}>🗑</button>
                </div>
              )}
            </For>
          </div>
        </div>
      </Show>

      {/* ── EDIT / VIEW shared toolbar ─────────────────────────────────────────── */}
      <Show when={mode() !== 'list'}>
        <div class="rb-toolbar">
          <button class="btn btn-sm btn-ghost" onClick={backToList}>← Reports</button>
          <span class="rb-divider" />
          <Show when={mode() === 'edit'} fallback={
            <>
              <span class="rb-title-view">{report.name || 'Untitled report'}</span>
              <span class="spacer" />
              <button class="btn btn-sm" onClick={() => setMode('edit')}>✎ Edit</button>
              <button class="btn btn-sm" onClick={printReport}>🖨 Print / PDF</button>
              <button class="btn btn-sm btn-primary" onClick={runAll} disabled={running()}>{running() ? 'Running…' : '▶ Run'}</button>
            </>
          }>
            <input class="input rb-name" placeholder="Report name" value={report.name} onInput={(e) => { setReport('name', e.currentTarget.value); markDirty(); }} />
            <span class="spacer" />
            <Show when={currentId() != null}>
              <button class="btn btn-sm" onClick={() => { setMode('view'); queueMicrotask(runAll); }}>▶ View</button>
            </Show>
            <button class="btn btn-sm" onClick={() => save(true)} title="Save as a new report">⧉ Save as</button>
            <button class="btn btn-sm btn-primary" classList={{ 'rb-dirty': dirty() }} onClick={() => save(false)}>💾 Save</button>
          </Show>
        </div>
      </Show>

      {/* ── EDIT ──────────────────────────────────────────────────────────────── */}
      <Show when={mode() === 'edit'}>
        <div class="rb-edit">
          {/* Settings */}
          <div class="rb-edit-section">
            <div class="rb-sec-h">Settings</div>
            <div class="rb-settings">
              <label class="rb-field"><span>Description</span>
                <input class="input" value={report.description} onInput={(e) => { setReport('description', e.currentTarget.value); markDirty(); }} placeholder="Optional" />
              </label>
              <label class="rb-field"><span>Connection</span>
                <select class="input" value={report.connectionId} onChange={(e) => { setReport('connectionId', e.currentTarget.value); setReport('database', ''); markDirty(); }}>
                  <option value="">— connection —</option>
                  <For each={conns() || []}>{(c) => <option value={c.id}>{c.name}</option>}</For>
                </select>
              </label>
              <label class="rb-field"><span>Database</span>
                <select class="input" value={report.database} onChange={(e) => { setReport('database', e.currentTarget.value); markDirty(); }} disabled={!report.connectionId}>
                  <option value="">— database —</option>
                  <For each={dbList()}>{(n) => <option value={n}>{n}</option>}</For>
                </select>
              </label>
            </div>
          </div>

          {/* Parameters */}
          <div class="rb-edit-section">
            <div class="rb-sec-h">Parameters <span class="muted">— bound as <code>@name</code> in block SQL</span>
              <button class="btn btn-sm btn-ghost rb-sec-add" onClick={addParam}>＋ Parameter</button>
            </div>
            <Show when={report.params.length === 0}><div class="muted rb-hint">No parameters. Add one to make the report interactive (e.g. a date range or company id).</div></Show>
            <For each={report.params}>
              {(p, i) => (
                <div class="rb-param">
                  <input class="input rb-p-name" placeholder="name" value={p.name} onInput={(e) => setParam(i(), 'name', e.currentTarget.value)} title="Reference in SQL as @name" />
                  <input class="input rb-p-label" placeholder="label" value={p.label} onInput={(e) => setParam(i(), 'label', e.currentTarget.value)} />
                  <select class="input rb-p-type" value={p.type} onChange={(e) => setParam(i(), 'type', e.currentTarget.value)}>
                    <option value="text">text</option><option value="number">number</option>
                    <option value="date">date</option><option value="select">select</option>
                  </select>
                  <input class="input rb-p-def" placeholder="default" value={p.default} onInput={(e) => setParam(i(), 'default', e.currentTarget.value)} />
                  <Show when={p.type === 'select'}>
                    <input class="input rb-p-opts" placeholder="options: a, b, c" value={(p.options || []).join(', ')}
                           onInput={(e) => setParam(i(), 'options', e.currentTarget.value.split(',').map((s) => s.trim()).filter(Boolean))} />
                  </Show>
                  <button class="rb-mini" title="Remove" onClick={() => removeParam(i())}>×</button>
                </div>
              )}
            </For>
          </div>

          {/* Blocks */}
          <div class="rb-edit-section">
            <div class="rb-sec-h">Blocks
              <span class="rb-sec-add rb-addblocks">
                <button class="btn btn-sm btn-ghost" onClick={() => addBlock('text')}>＋ Text</button>
                <button class="btn btn-sm btn-ghost" onClick={() => addBlock('query')}>＋ Query</button>
                <button class="btn btn-sm btn-ghost" onClick={() => addBlock('metric')}>＋ Metric</button>
              </span>
            </div>
            <Show when={report.blocks.length === 0}><div class="muted rb-hint">No blocks yet. Add a Text, Query, or Metric block.</div></Show>
            <For each={report.blocks}>
              {(b, i) => (
                <div class="rb-block-edit">
                  <div class="rb-block-bar">
                    <span class="rb-block-kind">{b.type}</span>
                    <Show when={b.type !== 'text'}>
                      <input class="input rb-block-title" placeholder="title" value={b.title} onInput={(e) => setBlock(i(), 'title', e.currentTarget.value)} />
                    </Show>
                    <span class="spacer" />
                    <Show when={b.type === 'query'}>
                      <select class="input rb-block-view" value={b.view} onChange={(e) => setBlock(i(), 'view', e.currentTarget.value)}>
                        <option value="table">Table</option><option value="chart">Chart</option>
                      </select>
                    </Show>
                    <Show when={b.type !== 'text'}>
                      <button class="btn btn-sm btn-ghost" onClick={() => previewBlock(b)} disabled={running()}>▶ Preview</button>
                    </Show>
                    <button class="rb-mini" title="Move up" onClick={() => moveBlock(i(), -1)}>↑</button>
                    <button class="rb-mini" title="Move down" onClick={() => moveBlock(i(), 1)}>↓</button>
                    <button class="rb-mini" title="Remove block" onClick={() => removeBlock(i())}>×</button>
                  </div>

                  <Show when={b.type === 'text'}>
                    <textarea class="input rb-textarea" rows="4" value={b.content} onInput={(e) => setBlock(i(), 'content', e.currentTarget.value)}
                              placeholder="# Heading  ·  ## Subheading  ·  **bold**  ·  plain text" />
                  </Show>
                  <Show when={b.type !== 'text'}>
                    <textarea class="input rb-sql" rows="3" value={b.sql} onInput={(e) => setBlock(i(), 'sql', e.currentTarget.value)} spellcheck={false}
                              placeholder="SELECT … (reference parameters as @name)" />
                  </Show>
                  <Show when={b.type === 'metric'}>
                    <input class="input rb-metric-label" placeholder="label (e.g. Total sales)" value={b.label} onInput={(e) => setBlock(i(), 'label', e.currentTarget.value)} />
                  </Show>

                  {/* Preview output */}
                  <Show when={blockResult(b.id)}>
                    <div class="rb-block-prev">
                      <Show when={blockResult(b.id).loading}><span class="muted">Running…</span></Show>
                      <Show when={blockResult(b.id).error}><div class="rb-err">{blockResult(b.id).error}</div></Show>
                      <Show when={!blockResult(b.id).loading && !blockResult(b.id).error}>
                        <Switch>
                          <Match when={b.type === 'metric'}>
                            <div class="rb-metric"><span class="rb-metric-v">{metricValue(blockResult(b.id))}</span></div>
                          </Match>
                          <Match when={b.type === 'query' && b.view === 'chart'}>
                            <div class="rb-chartwrap">
                              <ChartView columns={blockResult(b.id).columns} rows={blockResult(b.id).rows}
                                         initialSpec={b.chart} onSpecChange={(s) => setBlock(i(), 'chart', s)} title={b.title} />
                            </div>
                          </Match>
                          <Match when={true}>
                            {ResultTable(blockResult(b.id))}
                          </Match>
                        </Switch>
                      </Show>
                    </div>
                  </Show>
                </div>
              )}
            </For>
          </div>
        </div>
      </Show>

      {/* ── VIEW ──────────────────────────────────────────────────────────────── */}
      <Show when={mode() === 'view'}>
        <div class="rb-view">
          <div class="rb-doc">
            <h1 class="rb-doc-title">{report.name || 'Untitled report'}</h1>
            <Show when={report.description}><p class="rb-doc-desc">{report.description}</p></Show>

            {/* Parameter controls */}
            <Show when={report.params.length > 0}>
              <div class="rb-params-row rb-noprint">
                <For each={report.params}>
                  {(p) => (
                    <label class="rb-pctl">
                      <span>{p.label || p.name}</span>
                      <Switch fallback={
                        <input class="input" type={p.type === 'number' ? 'number' : p.type === 'date' ? 'date' : 'text'}
                               value={paramValues()[p.name] ?? ''} onInput={(e) => setParamValues({ ...paramValues(), [p.name]: e.currentTarget.value })} />
                      }>
                        <Match when={p.type === 'select'}>
                          <select class="input" value={paramValues()[p.name] ?? ''} onChange={(e) => setParamValues({ ...paramValues(), [p.name]: e.currentTarget.value })}>
                            <For each={p.options || []}>{(o) => <option value={o}>{o}</option>}</For>
                          </select>
                        </Match>
                      </Switch>
                    </label>
                  )}
                </For>
                <button class="btn btn-sm btn-primary" onClick={runAll} disabled={running()}>{running() ? 'Running…' : 'Apply'}</button>
              </div>
            </Show>

            {/* Blocks */}
            <For each={report.blocks}>
              {(b) => (
                <div class="rb-block">
                  <Switch>
                    <Match when={b.type === 'text'}>{TextBlock(b.content)}</Match>
                    <Match when={b.type === 'metric'}>
                      <div class="rb-metric">
                        <Show when={b.label}><span class="rb-metric-l">{b.label}</span></Show>
                        <span class="rb-metric-v">{blockState(blockResult(b.id), () => metricValue(blockResult(b.id)))}</span>
                      </div>
                    </Match>
                    <Match when={b.type === 'query'}>
                      <Show when={b.title}><h3 class="rb-block-h">{b.title}</h3></Show>
                      <Show when={blockResult(b.id)} fallback={<div class="muted rb-hint">Run the report to populate this block.</div>}>
                        <Show when={blockResult(b.id).loading}><div class="muted">Running…</div></Show>
                        <Show when={blockResult(b.id).error}><div class="rb-err">{blockResult(b.id).error}</div></Show>
                        <Show when={!blockResult(b.id).loading && !blockResult(b.id).error}>
                          <Show when={b.view === 'chart'} fallback={ResultTable(blockResult(b.id))}>
                            <div class="rb-chartwrap">
                              <Show when={chartModelOf(blockResult(b.id), b.chart).series.length > 0}
                                    fallback={<div class="muted rb-hint">Configure this chart's X/Y in the editor.</div>}>
                                <Chart model={chartModelOf(blockResult(b.id), b.chart)} />
                              </Show>
                            </div>
                          </Show>
                        </Show>
                      </Show>
                    </Match>
                  </Switch>
                </div>
              )}
            </For>
          </div>
        </div>
      </Show>
    </div>
  );
}

// ── small render helpers (module scope: pure of component state) ───────────────
const metricValue = (res) => {
  if (!res || !res.rows || res.rows.length === 0) return '—';
  const v = res.rows[0][0];
  if (v == null) return '—';
  const n = Number(v);
  return Number.isFinite(n) ? n.toLocaleString() : String(v);
};

const blockState = (res, render) => {
  if (!res) return '—';
  if (res.loading) return '…';
  if (res.error) return '!';
  return render();
};

const chartModelOf = (res, spec) => buildChartModel(res?.columns || [], res?.rows || [], spec || {});

// A compact, read-only result table (capped) for report query blocks.
function ResultTable(res) {
  const cols = res.columns || [];
  const rows = (res.rows || []).slice(0, 500);
  return (
    <div class="rb-table-wrap">
      <table class="rb-table">
        <thead><tr><For each={cols}>{(c) => <th>{c}</th>}</For></tr></thead>
        <tbody>
          <For each={rows}>
            {(row) => <tr><For each={row}>{(cell) => <td>{cell === null ? <span class="rb-null">NULL</span> : String(cell)}</td>}</For></tr>}
          </For>
        </tbody>
      </table>
      <Show when={(res.rows || []).length > 500}><div class="muted rb-hint">Showing first 500 of {res.rows.length} rows.</div></Show>
    </div>
  );
}

// Minimal, dependency-free markdown: '# '/'## ' headings, **bold**, blank lines.
function TextBlock(content) {
  const lines = String(content || '').split('\n');
  const renderInline = (s) => {
    const parts = s.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((p) => (p.startsWith('**') && p.endsWith('**')) ? <strong>{p.slice(2, -2)}</strong> : p);
  };
  return (
    <div class="rb-text">
      <For each={lines}>
        {(ln) => (
          <Switch fallback={<p>{renderInline(ln)}</p>}>
            <Match when={ln.startsWith('## ')}><h4>{ln.slice(3)}</h4></Match>
            <Match when={ln.startsWith('# ')}><h3>{ln.slice(2)}</h3></Match>
            <Match when={ln.trim() === ''}><div class="rb-text-gap" /></Match>
          </Switch>
        )}
      </For>
    </div>
  );
}
